import cloneDeep from "lodash.clonedeep";
import isEqual from "lodash.isequal";
import defaultIdGen from "../../utils/idGen";
import { checkDeep } from "../../utils/tree";
import { withEndpointRefs } from "./variants/variantChanges";

// The structural operations of a property (variants, pages, endpoints) as
// draft rows built by the client: no modifiche, no request. Each builder is
// pure: it reads the rows it is given and returns the ops of ONE
// draft.change (all or none), one per row (no key twice, no edit of a row
// created by the same change):
//
//   { kind: "create", accessor, row }
//   { kind: "edit",   accessor, id, updater(row, prior) => row }
//   { kind: "remove", accessor, id, reference }   // reference: endpoints
//   ...and deps: [key] on each op when the tree is in the form (treeVia),
//   inherit on the create of a new endpoint (the property's params at the
//   Save)
//
// A remove is emitted only for a row that exists (draft.change throws for a
// missing one). The updaters work on the row they get (the draft row when
// the row is drafted), never on a row read when the operation was built.
//
// Common inputs:
// - peek(accessor, id): the row as the UI sees it (MemoryDB, i.e. the draft
//   row when drafted), undefined when there is none. Not changed.
// - stored(accessor, id): the stored row the draft started from (storedRow
//   of useScopeEdit): the row, or none (null or undefined) for a row created
//   in the draft. An endpoint edit compares with it, so that an operation
//   undone (add a variant, then delete it) gives the stored row back and
//   leaves no draft.
// - doRefs(row): the refs of an endpoint (doEndpointRefs of
//   src/components/endpoint). Passed in: that module is not pure.
// - idGen(): the id of a new row (src/utils/idGen by default).
// - treeVia: how the tree of the property changes.
//   - "op": an edit of properties:<propertyId> (the variant tabs, where the
//     open form is a block);
//   - "form": the new tree is returned ({ ops, tree }), the caller sets it
//     with setFieldValue("tree") (the property page, where the property is
//     the open form: its row can't be drafted). The ops of a structural
//     operation (a row created or removed) then carry deps:
//     ["properties:<propertyId>"]: the property is part of the operation
//     (its tree refers to the rows), as when it is an op, so that a reload
//     or a conflict of it discards the draft instead of leaving the rows
//     without their tree nodes.
//   The tree read is `tree`, or the tree of the property row.
//
// The copy of a page to another property is disabled (decision of the
// user): no builder.

// *************************************************************************
// OPS
// *************************************************************************

const createOp = (accessor, row, inherit) =>
  inherit
    ? { kind: "create", accessor, row, inherit }
    : { kind: "create", accessor, row };

const editOp = (accessor, id, updater) => ({
  kind: "edit",
  accessor,
  id,
  updater
});

const removeOp = (accessor, id, reference) =>
  reference === undefined
    ? { kind: "remove", accessor, id }
    : { kind: "remove", accessor, id, reference };

// The ops in the order the rows depend on each other: the created rows
// first (an endpoint requires its blocks), then the edits, then the removes.
const ORDER = { create: 0, edit: 1, remove: 2 };
const ordered = (ops) => [...ops].sort((a, b) => ORDER[a.kind] - ORDER[b.kind]);

// *************************************************************************
// INPUTS
// *************************************************************************

function requireFn(name, fn) {
  if (typeof fn !== "function")
    throw new Error(`structure: ${name} must be a function`);
}

// An id no row of the table has (the store refuses a create of an id that
// exists).
function freshId(idGen, peek, accessor) {
  for (let attempt = 0; attempt < 20; attempt++) {
    const id = idGen();
    if (peek(accessor, id) === undefined) return id;
  }
  throw new Error(`structure: no free id for a new row of ${accessor}`);
}

const treeOf = (tree, peek, propertyId) =>
  tree ?? peek("properties", propertyId)?.tree;

// The keys of the form values a new endpoint takes from its property (as
// the server did: modifiche.ts, create endpoint, which read the property
// saved in the same batch).
const ENDPOINT_FROM_PROPERTY = [
  "params",
  "paramsId",
  "input",
  "output",
  "logOperation"
];

// *************************************************************************
// TREE (keyed by node value)
// *************************************************************************

const isFolder = (node) => Array.isArray(node?.children);

// The node of a value: { node, parent (null at the top), siblings (the
// array it is in), index }, or null.
function locate(nodes, value, parent = null) {
  for (let index = 0; index < nodes.length; index++) {
    const node = nodes[index];
    if (node?.value === value) return { node, parent, siblings: nodes, index };
    if (node?.children?.length > 0) {
      const found = locate(node.children, value, node);
      if (found) return found;
    }
  }
  return null;
}

function folderOf(tree, value) {
  const found = locate(tree, value);
  if (!found || !isFolder(found.node))
    throw new Error(`structure: no folder ${value} in the tree`);
  return found.node;
}

// Appends a node to the children of a folder (after the node `after` when
// it is one of them).
function insertNode(tree, folderId, node, after) {
  const folder = folderOf(tree, folderId);
  const index = after
    ? folder.children.findIndex((child) => child.value === after)
    : -1;

  if (index < 0) folder.children.push(node);
  else folder.children.splice(index + 1, 0, node);
}

// Removes the node of a value, wherever it is (nothing when it is not
// there).
function removeNode(tree, value) {
  const found = locate(tree, value);
  if (found) found.siblings.splice(found.index, 1);
}

function relabelNode(tree, value, label) {
  const found = locate(tree, value);
  if (found) found.node.label = label;
}

// The change of the tree of the property: an op on its row, or the new tree
// of the form. change(tree) changes a tree in place. It is first run on a
// copy of the tree read, so that a change that can't be done throws here.
function treeChange({ treeVia, propertyId, tree, change }) {
  if (treeVia !== "op" && treeVia !== "form")
    throw new Error(
      `structure: treeVia must be "op" or "form", not ${treeVia}`
    );
  if (!Array.isArray(tree))
    throw new Error(`structure: no tree for the property ${propertyId}`);

  const next = cloneDeep(tree);
  change(next);

  if (treeVia === "form")
    return { tree: next, deps: [`properties:${propertyId}`] };

  return {
    op: editOp("properties", propertyId, (row) => {
      if (!Array.isArray(row.tree))
        throw new Error(`structure: the property ${propertyId} has no tree`);
      change(row.tree);
      return row;
    })
  };
}

const keyOf = (op) =>
  `${op.accessor}:${op.kind === "create" ? op.row?.id : op.id}`;

// The ops of one draft.change: one per key, so no edit or remove either of
// a row the same change creates (a mistake of a builder: thrown).
function assertOneChange(ops) {
  const keys = new Set();
  for (const op of ops) {
    const key = keyOf(op);
    if (keys.has(key)) throw new Error(`structure: ${key} twice in one change`);
    keys.add(key);
  }
}

// { ops, tree } for "form", { ops } for "op" (plus the extra fields). With
// the tree in the form, the ops of a structural operation depend on the
// property (deps).
function result(ops, treeResult, extra = {}) {
  let all = treeResult.op ? [...ops, treeResult.op] : ops;
  if (treeResult.deps && all.some((op) => op.kind !== "edit"))
    all = all.map((op) => ({ ...op, deps: treeResult.deps }));
  assertOneChange(all);
  return {
    ops: ordered(all),
    ...(treeResult.tree && { tree: treeResult.tree }),
    ...extra
  };
}

// *************************************************************************
// ENDPOINT: DERIVED FIELDS
// *************************************************************************

// withEndpointRefs builds the refs again only when their inputs (blocks,
// params, decorators, conversion packs) differ between two rows: whether
// it calls doRefs says whether they do.
const DIFFERENT = Symbol("different");
const sameRefInputs = (a, b) =>
  withEndpointRefs(a, b, () => DIFFERENT) !== DIFFERENT;

// The value of a key of the stored row (removed when the stored row has
// none: a key set to undefined makes the rows differ).
function restoreKey(row, stored, key) {
  if (key in stored) row[key] = cloneDeep(stored[key]);
  else delete row[key];
}

function restoreRequire(row, stored) {
  if (stored.refs && "require" in stored.refs) {
    row.refs = { ...(row.refs ?? {}), require: cloneDeep(stored.refs.require) };
    return;
  }
  if (!row.refs) return;
  delete row.refs.require;
  if (!stored.refs && Object.keys(row.refs).length === 0) delete row.refs;
}

// The fields of an endpoint derived from what an operation changed (row:
// the new row, changed in place; before: the row the operation started
// from; stored: the stored row, none for an endpoint created in the
// draft).
// Each one goes back to the stored value when what it is derived from is
// back to the stored one: the store compares rows strictly, so an undone
// operation then leaves no draft.
// - refs.require: doRefs when its inputs changed (withEndpointRefs);
// - prevReference: the stored reference after a rename (the reference of a
//   new endpoint), the stored prevReference otherwise;
// - haveScheduledVariants: changed only when it no longer says whether a
//   variant is scheduled, as 1/0 (the server and the full load write
//   booleans or 1/0: a recomputed value would not compare equal).
export function deriveEndpoint(row, before, stored, doRefs) {
  if (stored && sameRefInputs(stored, row)) restoreRequire(row, stored);
  else if (!sameRefInputs(before, row)) {
    requireFn("doRefs", doRefs);
    const built = doRefs(cloneDeep(row));
    row.refs = { ...(row.refs ?? {}), require: built.refs.require };
  }

  if (stored) {
    if (row.reference === stored.reference)
      restoreKey(row, stored, "prevReference");
    else row.prevReference = stored.reference;
  } else if (row.reference !== undefined) {
    row.prevReference = row.reference;
  }

  const scheduled = (row.variants ?? []).some((variant) => variant?.scheduled);
  if (stored && Boolean(stored.haveScheduledVariants) === scheduled)
    restoreKey(row, stored, "haveScheduledVariants");
  else if (Boolean(row.haveScheduledVariants) !== scheduled)
    row.haveScheduledVariants = scheduled ? 1 : 0;

  return row;
}

// The updater of an endpoint: change(row) on the row it gets, then the
// derived fields.
const endpointUpdater = (stored, doRefs, change) => (row) => {
  const before = cloneDeep(row);
  change(row);
  return deriveEndpoint(row, before, stored, doRefs);
};

// A new variant at the end. Its priority: 1 on a stored endpoint (as the
// server's update did), its index on an endpoint created in the draft
// (no stored row, as the server's create did).
function appendVariant(row, blockId, stored) {
  const variants = row.variants ?? [];
  row.variants = [
    ...variants,
    {
      blockId,
      condition: "*",
      priority: stored ? 1 : variants.length,
      active: false
    }
  ];
  return row;
}

function removeVariant(row, blockId) {
  if (row.variants?.some((variant) => variant.blockId === blockId))
    row.variants = row.variants.filter(
      (variant) => variant.blockId !== blockId
    );
  return row;
}

const hasVariant = (endpoint, blockId) =>
  Boolean(endpoint?.variants?.some((variant) => variant.blockId === blockId));

// *************************************************************************
// ROWS
// *************************************************************************

// A new page (as the server's create page did, modifiche.ts).
export function newPageRow({ id, propertyId, title, endpointId }) {
  return {
    id,
    propertyId,
    title,
    description: `Please Edit description for Page "${title}"`,
    enabledPagesId: [endpointId],
    isPage: true,
    blocks: []
  };
}

// The copy of a block as a new page: without what the server owns or the
// client computes (version, lastSaved, refs.belongs, refsComputed).
export function copiedPageRow(source, { id, propertyId, title, endpointId }) {
  const { version, lastSaved, refsComputed, refs, ...row } = cloneDeep(source);
  const { belongs, ...rest } = refs ?? {};

  return {
    ...row,
    ...(Object.keys(rest).length > 0 && { refs: rest }),
    id,
    title,
    propertyId,
    enabledPagesId: [endpointId],
    isPage: true
  };
}

// The fields of the property a new endpoint copied from `values` when it
// was created, as they were.
const fromProperty = (values) => {
  const copied = {};
  for (const key of ENDPOINT_FROM_PROPERTY) {
    if (values?.[key] !== undefined) copied[key] = cloneDeep(values[key]);
  }
  return copied;
};

// The fields a new endpoint takes from its property at the Save (the
// inherit of its create op, see draft.change): the server read the
// property saved in the same batch, so a change of the property's params
// after the page was created went to the endpoint. apply(row, property)
// gives the endpoint the property's value of each field the endpoint still
// has as it copied it at creation (a field changed on the endpoint is
// kept), and its refs again. property: the property row the batch saves
// (the form's values when its Item saves; else its draft or stored row).
function inheritFromProperty({ propertyId, values, doRefs }) {
  const copied = fromProperty(values);

  return {
    accessor: "properties",
    id: propertyId,
    apply: (row, property) => {
      const before = cloneDeep(row);
      for (const key of ENDPOINT_FROM_PROPERTY) {
        if (!isEqual(row[key], copied[key])) continue;
        if (property?.[key] !== undefined) row[key] = cloneDeep(property[key]);
        else delete row[key];
      }
      return deriveEndpoint(row, before, null, doRefs);
    }
  };
}

// A new endpoint (as the server's create endpoint did, modifiche.ts), with
// the params, input and output of the property's form values (its MemoryDB
// row is stale while the form is open); the Save gives it the ones of the
// property it saves (inheritFromProperty).
function newEndpointRow({ id, reference, propertyId, pages, values, doRefs }) {
  const [blockId, ...variants] = pages;
  const row = {
    id,
    active: true,
    title: reference,
    description: "",
    reference,
    propertyId,
    blockId,
    variants: variants.map((variantId, priority) => ({
      blockId: variantId,
      priority,
      condition: "*",
      active: false
    }))
  };

  Object.assign(row, fromProperty(values));

  return deriveEndpoint(row, {}, null, doRefs);
}

// *************************************************************************
// VARIANT TABS (the open form is a block: treeVia "op" by default)
// *************************************************************************

// Add a variant to the endpoint: a new page N (created), appended to the
// variants of the endpoint E and to the node of E in the tree.
// Returns { ops, tree?, blockId }.
export function addVariant({
  propertyId,
  endpointId,
  title,
  peek,
  stored,
  doRefs,
  idGen = defaultIdGen,
  treeVia = "op",
  tree
}) {
  requireFn("peek", peek);
  requireFn("stored", stored);
  requireFn("doRefs", doRefs);

  const endpoint = peek("endpoints", endpointId);
  if (!endpoint) throw new Error(`structure: no endpoint ${endpointId}`);

  const blockId = freshId(idGen, peek, "blocks");
  const label = title ?? `Variant ${(endpoint.variants?.length ?? 0) + 1}`;
  const storedEndpoint = stored("endpoints", endpointId);

  const ops = [
    createOp(
      "blocks",
      newPageRow({ id: blockId, propertyId, title: label, endpointId })
    ),
    editOp(
      "endpoints",
      endpointId,
      endpointUpdater(storedEndpoint, doRefs, (row) =>
        appendVariant(row, blockId, storedEndpoint)
      )
    )
  ];

  const treeResult = treeChange({
    treeVia,
    propertyId,
    tree: treeOf(tree, peek, propertyId),
    change: (tree) => insertNode(tree, endpointId, { value: blockId, label })
  });

  return result(ops, treeResult, { blockId });
}

// Copy of a variant (or of the default page) of the endpoint, keyed by the
// block copied:
// - the default page: the copy is the first variant ({ blockId, condition:
//   "*", priority: 1 }, as today);
// - a variant: a copy of its variant (condition, scheduling, active...) right
//   after it.
// The node of the copy goes right after the node of the block copied.
// The copy is built from the block row as the UI sees it: copying the open
// page, stash its form first. Returns { ops, tree?, blockId }.
export function copyVariant({
  propertyId,
  endpointId,
  blockId: sourceId,
  title,
  peek,
  stored,
  doRefs,
  idGen = defaultIdGen,
  treeVia = "op",
  tree
}) {
  requireFn("peek", peek);
  requireFn("stored", stored);
  requireFn("doRefs", doRefs);

  const endpoint = peek("endpoints", endpointId);
  if (!endpoint) throw new Error(`structure: no endpoint ${endpointId}`);
  const source = peek("blocks", sourceId);
  if (!source) throw new Error(`structure: no block ${sourceId}`);
  if (endpoint.blockId !== sourceId && !hasVariant(endpoint, sourceId))
    throw new Error(`structure: ${sourceId} is not a page of ${endpointId}`);

  const blockId = freshId(idGen, peek, "blocks");
  const label = title ?? `${source.title}-[COPY]`;
  const storedEndpoint = stored("endpoints", endpointId);

  const insertCopy = (row) => {
    const variants = row.variants ?? [];

    if (row.blockId === sourceId) {
      row.variants = [{ blockId, condition: "*", priority: 1 }, ...variants];
      return row;
    }

    const index = variants.findIndex((variant) => variant.blockId === sourceId);
    if (index < 0)
      throw new Error(`structure: ${sourceId} is not a page of ${row.id}`);
    const copy = { ...cloneDeep(variants[index]), blockId };
    row.variants = [
      ...variants.slice(0, index + 1),
      copy,
      ...variants.slice(index + 1)
    ];
    return row;
  };

  const ops = [
    createOp(
      "blocks",
      copiedPageRow(source, {
        id: blockId,
        propertyId,
        title: label,
        endpointId
      })
    ),
    editOp(
      "endpoints",
      endpointId,
      endpointUpdater(storedEndpoint, doRefs, insertCopy)
    )
  ];

  const treeResult = treeChange({
    treeVia,
    propertyId,
    tree: treeOf(tree, peek, propertyId),
    change: (tree) =>
      insertNode(tree, endpointId, { value: blockId, label }, sourceId)
  });

  return result(ops, treeResult, { blockId });
}

// Delete a variant of the endpoint, keyed by its block (not by the index of
// its tab): out of the variants of E, out of the tree, the block removed (a
// block created in the draft just leaves it). The default page can't be
// deleted. Returns { ops, tree? }.
export function deleteVariant({
  propertyId,
  endpointId,
  blockId,
  peek,
  stored,
  doRefs,
  treeVia = "op",
  tree
}) {
  requireFn("peek", peek);
  requireFn("stored", stored);
  requireFn("doRefs", doRefs);

  const endpoint = peek("endpoints", endpointId);
  if (!endpoint) throw new Error(`structure: no endpoint ${endpointId}`);
  if (endpoint.blockId === blockId)
    throw new Error(
      `structure: the default page of ${endpointId} can't be deleted`
    );

  const ops = [];

  if (hasVariant(endpoint, blockId)) {
    ops.push(
      editOp(
        "endpoints",
        endpointId,
        endpointUpdater(stored("endpoints", endpointId), doRefs, (row) =>
          removeVariant(row, blockId)
        )
      )
    );
  }

  if (peek("blocks", blockId) !== undefined)
    ops.push(removeOp("blocks", blockId));

  const treeResult = treeChange({
    treeVia,
    propertyId,
    tree: treeOf(tree, peek, propertyId),
    change: (tree) => removeNode(tree, blockId)
  });

  return result(ops, treeResult);
}

// *************************************************************************
// TREE OF THE PROPERTY (the open form is the property: treeVia "form" by
// default)
// *************************************************************************

// A new page in a folder: "Default" for the first one, "Variant <n>" after.
// - the folder is an endpoint: the page is appended to its variants;
// - otherwise the endpoint is created (id: the folder's value, reference:
//   its label), with the params, input and output of the property's form
//   `values` (taken again from the property the Save sends, while the
//   endpoint has them as copied: inheritFromProperty); its default page is
//   the first page of the folder.
// Returns { ops, tree?, blockId }.
export function createPage({
  propertyId,
  folderId,
  values,
  peek,
  stored,
  doRefs,
  idGen = defaultIdGen,
  treeVia = "form",
  tree
}) {
  requireFn("peek", peek);
  requireFn("stored", stored);
  requireFn("doRefs", doRefs);

  const currentTree = treeOf(tree, peek, propertyId);
  const folder = folderOf(currentTree ?? [], folderId);
  const children = folder.children;
  const label = children.length > 0 ? `Variant ${children.length}` : "Default";
  const blockId = freshId(idGen, peek, "blocks");
  const endpoint = peek("endpoints", folderId);

  const ops = [
    createOp(
      "blocks",
      newPageRow({
        id: blockId,
        propertyId,
        title: label,
        endpointId: folderId
      })
    )
  ];

  if (endpoint) {
    const storedEndpoint = stored("endpoints", folderId);
    ops.push(
      editOp(
        "endpoints",
        folderId,
        endpointUpdater(storedEndpoint, doRefs, (row) =>
          row.blockId
            ? appendVariant(row, blockId, storedEndpoint)
            : Object.assign(row, { blockId })
        )
      )
    );
  } else {
    if (!values)
      throw new Error(
        "structure: a new endpoint needs the property's form values"
      );
    const pages = [
      ...children
        .filter((child) => !isFolder(child))
        .map((child) => child.value),
      blockId
    ];
    ops.push(
      createOp(
        "endpoints",
        newEndpointRow({
          id: folderId,
          reference: folder.label,
          propertyId,
          pages,
          values,
          doRefs
        }),
        inheritFromProperty({ propertyId, values, doRefs })
      )
    );
  }

  const treeResult = treeChange({
    treeVia,
    propertyId,
    tree: currentTree,
    change: (tree) => insertNode(tree, folderId, { value: blockId, label })
  });

  return result(ops, treeResult, { blockId });
}

// Copy of a page into an endpoint of the same property (its own or
// another): the copy of the block (copiedPageRow) becomes a new variant of
// the destination endpoint, at the end and not active, as an added variant
// (priority 1, its index on an endpoint created in the draft); its node
// goes at the end of the destination node. The destination must be an
// endpoint. Returns { ops, tree?, blockId }.
export function copyPage({
  propertyId,
  blockId: sourceId,
  destEndpointId,
  title,
  peek,
  stored,
  doRefs,
  idGen = defaultIdGen,
  treeVia = "form",
  tree
}) {
  requireFn("peek", peek);
  requireFn("stored", stored);
  requireFn("doRefs", doRefs);

  const source = peek("blocks", sourceId);
  if (!source) throw new Error(`structure: no block ${sourceId}`);
  if (!peek("endpoints", destEndpointId))
    throw new Error(
      `structure: no endpoint ${destEndpointId} to copy the page into`
    );

  const blockId = freshId(idGen, peek, "blocks");
  const label = title ?? `${source.title}-[COPY]`;
  const storedEndpoint = stored("endpoints", destEndpointId);

  const ops = [
    createOp(
      "blocks",
      copiedPageRow(source, {
        id: blockId,
        propertyId,
        title: label,
        endpointId: destEndpointId
      })
    ),
    editOp(
      "endpoints",
      destEndpointId,
      endpointUpdater(storedEndpoint, doRefs, (row) =>
        appendVariant(row, blockId, storedEndpoint)
      )
    )
  ];

  const treeResult = treeChange({
    treeVia,
    propertyId,
    tree: treeOf(tree, peek, propertyId),
    change: (tree) =>
      insertNode(tree, destEndpointId, { value: blockId, label })
  });

  return result(ops, treeResult, { blockId });
}

// Delete a node of the tree, within what checkDeep (src/utils/tree.js)
// allows:
// - a page: out of the variants of its endpoint, its block removed (not the
//   default page of an endpoint);
// - a folder with no subfolders and at most one page: its endpoint removed
//   (with the stored reference, the one the server knows), the block of its
//   page removed.
// Rows that don't exist are left alone; rows created in the draft just
// leave it. Returns { ops, tree? }.
export function deletePageOrEndpoint({
  propertyId,
  nodeId,
  peek,
  stored,
  doRefs,
  treeVia = "form",
  tree
}) {
  requireFn("peek", peek);
  requireFn("stored", stored);

  const currentTree = treeOf(tree, peek, propertyId);
  const found = locate(currentTree ?? [], nodeId);
  if (!found) throw new Error(`structure: no node ${nodeId} in the tree`);

  const { node, parent } = found;
  const ops = [];

  if (isFolder(node)) {
    if (!checkDeep(node))
      throw new Error(
        `structure: ${node.label} has subfolders or more than one page: it can't be deleted`
      );

    for (const child of node.children) {
      if (!isFolder(child) && peek("blocks", child.value) !== undefined)
        ops.push(removeOp("blocks", child.value));
    }

    const endpoint = peek("endpoints", nodeId);
    if (endpoint) {
      const storedEndpoint = stored("endpoints", nodeId);
      ops.push(
        removeOp(
          "endpoints",
          nodeId,
          storedEndpoint?.reference ?? endpoint.reference
        )
      );
    }
  } else {
    const endpoint = parent ? peek("endpoints", parent.value) : undefined;
    if (endpoint?.blockId === nodeId)
      throw new Error(
        `structure: the default page of ${endpoint.id} can't be deleted`
      );

    if (hasVariant(endpoint, nodeId)) {
      requireFn("doRefs", doRefs);
      ops.push(
        editOp(
          "endpoints",
          endpoint.id,
          endpointUpdater(stored("endpoints", endpoint.id), doRefs, (row) =>
            removeVariant(row, nodeId)
          )
        )
      );
    }

    if (peek("blocks", nodeId) !== undefined)
      ops.push(removeOp("blocks", nodeId));
  }

  const treeResult = treeChange({
    treeVia,
    propertyId,
    tree: currentTree,
    change: (tree) => removeNode(tree, nodeId)
  });

  return result(ops, treeResult);
}

// Rename a page: the title of its block only (enabledPagesId and the rest
// are kept), and the label of its node. Returns { ops, tree? }.
export function renamePage({
  propertyId,
  blockId,
  title,
  peek,
  treeVia = "form",
  tree
}) {
  requireFn("peek", peek);

  const ops = [];
  if (peek("blocks", blockId) !== undefined) {
    ops.push(
      editOp("blocks", blockId, (row) => {
        row.title = title;
        return row;
      })
    );
  }

  const treeResult = treeChange({
    treeVia,
    propertyId,
    tree: treeOf(tree, peek, propertyId),
    change: (tree) => relabelNode(tree, blockId, title)
  });

  return result(ops, treeResult);
}

// Rename an endpoint (the label of its folder): its reference, with
// prevReference the stored reference; renamed back to the stored reference,
// the stored prevReference again (the row is the stored one). An endpoint
// created in the draft (no stored row) takes the label as its title too, as
// it had when created (the server's create endpoint: title = reference). A
// folder with no endpoint changes in the tree only. Returns { ops, tree? }.
export function renameEndpoint({
  propertyId,
  endpointId,
  reference,
  peek,
  stored,
  doRefs,
  treeVia = "form",
  tree
}) {
  requireFn("peek", peek);
  requireFn("stored", stored);

  const ops = [];
  if (peek("endpoints", endpointId)) {
    const storedEndpoint = stored("endpoints", endpointId);
    ops.push(
      editOp(
        "endpoints",
        endpointId,
        endpointUpdater(storedEndpoint, doRefs, (row) => {
          row.reference = reference;
          if (!storedEndpoint) row.title = reference;
          return row;
        })
      )
    );
  }

  const treeResult = treeChange({
    treeVia,
    propertyId,
    tree: treeOf(tree, peek, propertyId),
    change: (tree) => relabelNode(tree, endpointId, reference)
  });

  return result(ops, treeResult);
}
